import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, ShieldCheck, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import { supabase } from "../supabaseClient";

export default function ResetPassword() {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState("");
    const [isValidSession, setIsValidSession] = useState(false);
    const [checkingSession, setCheckingSession] = useState(true);
    const navigate = useNavigate();

    // Listen for the PASSWORD_RECOVERY event from Supabase
    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, session) => {
                if (event === "PASSWORD_RECOVERY") {
                    setIsValidSession(true);
                    setCheckingSession(false);
                } else if (event === "SIGNED_IN" && session) {
                    // User may already be signed in via the recovery link
                    setIsValidSession(true);
                    setCheckingSession(false);
                }
            }
        );

        // Handle the hash-based token from Supabase recovery emails
        const handleRecoveryToken = async () => {
            const hash = window.location.hash;

            if (hash && hash.includes("type=recovery")) {
                // Parse the hash fragment to extract tokens
                const params = new URLSearchParams(hash.substring(1));
                const accessToken = params.get("access_token");
                const refreshToken = params.get("refresh_token");

                if (accessToken && refreshToken) {
                    const { error } = await supabase.auth.setSession({
                        access_token: accessToken,
                        refresh_token: refreshToken,
                    });

                    if (!error) {
                        setIsValidSession(true);
                        setCheckingSession(false);
                        // Clean up the hash from the URL
                        window.history.replaceState(null, "", window.location.pathname);
                        return;
                    }
                }
            }

            // Fallback: check if there's already a session
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                setIsValidSession(true);
            }
            setCheckingSession(false);
        };

        handleRecoveryToken();

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const passwordChecks = {
        minLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasNumber: /\d/.test(password),
        passwordsMatch: password === confirmPassword && confirmPassword.length > 0,
    };

    const isFormValid = Object.values(passwordChecks).every(Boolean);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid) {
            setError("Please meet all password requirements.");
            return;
        }

        setError("");
        setIsLoading(true);

        try {
            const { error: supabaseError } = await supabase.auth.updateUser({
                password: password,
            });

            if (supabaseError) {
                setError(supabaseError.message);
                setIsLoading(false);
                return;
            }

            setIsLoading(false);
            setIsSuccess(true);

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate("/login");
            }, 3000);
        } catch (err) {
            setError("Something went wrong. Please try again.");
            setIsLoading(false);
        }
    };

    // Loading state while checking session
    if (checkingSession) {
        return (
            <div className="flex justify-center items-center py-20 px-4">
                <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-[2rem] p-8 md:p-12 w-full max-w-lg text-center">
                    <svg className="animate-spin w-8 h-8 text-accent mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-secondary dark:text-gray-400">Verifying your reset link...</p>
                </div>
            </div>
        );
    }

    // Invalid or expired link
    if (!isValidSession && !checkingSession) {
        return (
            <div className="flex justify-center items-center py-20 px-4">
                <SEO title="Invalid Reset Link" description="This password reset link is invalid or has expired." />
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-[2rem] p-8 md:p-12 w-full max-w-lg text-center"
                >
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                        <ShieldCheck className="w-8 h-8" />
                    </div>
                    <h1 className="text-2xl font-black text-primary dark:text-white mb-3">
                        Invalid or Expired Link
                    </h1>
                    <p className="text-secondary dark:text-gray-400 mb-8">
                        This password reset link is no longer valid. Please request a new one.
                    </p>
                    <Link
                        to="/forgot-password"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-light text-white font-bold rounded-full transition-all duration-300 transform hover:-translate-y-1 active:scale-95"
                    >
                        Request New Link
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="flex justify-center items-center py-20 px-4">
            <SEO
                title="Create New Password"
                description="Enter and confirm your new blinqbooks account password."
            />

            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className={`bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-[2rem] p-8 md:p-12 w-full max-w-lg transition-all duration-500 relative overflow-hidden ${isLoading ? 'scale-[1.02]' : ''}`}
            >
                {/* Decorative background elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-accent/5 rounded-full blur-3xl pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-green-400/5 rounded-full blur-3xl pointer-events-none"></div>

                {/* Header */}
                <div className="text-center mb-10 relative z-10">
                    <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                        <Lock className="w-8 h-8 text-accent" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-black text-primary dark:text-white mb-3">
                        {isSuccess ? 'Password Updated!' : 'Set New Password'}
                    </h1>
                    <p className="text-secondary dark:text-gray-400 text-sm md:text-base max-w-sm mx-auto">
                        {isSuccess
                            ? "Your password has been updated successfully."
                            : "Create a strong password to secure your account."
                        }
                    </p>
                </div>

                {!isSuccess ? (
                    <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                        {/* New Password */}
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                                New Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setError("");
                                    }}
                                    placeholder="••••••••"
                                    required
                                    disabled={isLoading}
                                    className="w-full pl-12 pr-12 py-4 rounded-xl bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-accent outline-none transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setError("");
                                    }}
                                    placeholder="••••••••"
                                    required
                                    disabled={isLoading}
                                    className="w-full pl-12 pr-12 py-4 rounded-xl bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-accent outline-none transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Password Requirements */}
                        <div className="p-4 rounded-xl bg-slate-500/5 dark:bg-white/5 space-y-2">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Password Requirements</p>
                            {[
                                { check: passwordChecks.minLength, label: "At least 8 characters" },
                                { check: passwordChecks.hasUppercase, label: "One uppercase letter" },
                                { check: passwordChecks.hasNumber, label: "One number" },
                                { check: passwordChecks.passwordsMatch, label: "Passwords match" },
                            ].map((req, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <CheckCircle2
                                        className={`w-4 h-4 transition-colors ${req.check ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'
                                            }`}
                                    />
                                    <span className={`text-xs transition-colors ${req.check ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                                        }`}>
                                        {req.label}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {error && (
                            <motion.p
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-red-500 text-xs ml-1"
                            >
                                {error}
                            </motion.p>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || !isFormValid}
                            className={`w-full py-4 mt-4 font-bold text-white rounded-full transition-all duration-300 flex items-center justify-center gap-2 ${isLoading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : !isFormValid
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-accent hover:bg-accent-hover transform hover:-translate-y-1 active:scale-95'
                                }`}
                        >
                            {isLoading ? (
                                <svg className="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <>Update Password</>
                            )}
                        </button>
                    </form>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="text-center py-6 relative z-10"
                    >
                        <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="w-10 h-10" />
                        </div>
                        <p className="text-secondary dark:text-gray-300 mb-8">
                            You'll be redirected to the login page in a moment...
                        </p>
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 text-sm text-accent font-bold hover:underline group"
                        >
                            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                            Go to Login now
                        </Link>
                    </motion.div>
                )}
            </motion.div>
        </div>
    );
}
